import CloseIcon from "@mui/icons-material/Close";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  MenuItem,
  Select,
  Stack,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import axios from "axios";
import { forwardRef, useImperativeHandle, useState } from "react";
import { useSelector } from "react-redux";
import AppLoader from "../../../components/AppLoader";
import GlobalButton from "../../../components/CustomButton";
import Label from "../../../components/Label";
import TextInput from "../../../components/TextInputs";
import UploadImageAvatar from "../../../components/UploadImage";
import { baseUrl } from "../../../services";
import {
  createCompanyUser,
  updateCompanyUser,
} from "../../../services/company/companyUsersApiFn";
import { getAllCompanyRole } from "../../../services/company/roleApiFn";
import { handleOpenSnackBar } from "../../../utils";
import { t } from "i18next";

const useStyle = {
  selectStyle: {
    "&.MuiOutlinedInput-root .MuiSelect-outlined": {
      display: "flex",
      alignItems: "center",
      padding: "12px 14px",
    },
    "&.MuiOutlinedInput-root ": {
      "& .MuiOutlinedInput-notchedOutline": {
        border: `1px solid #7922D8 `,
      },
      "&:hover .MuiOutlinedInput-notchedOutline": {
        border: `1px solid #7922D8 `,
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        border: `1px solid #7922D8 `,
      },
    },
    "&.Mui-error ": {
      "& .MuiOutlinedInput-notchedOutline": {
        border: `1px solid #d32f2f `,
      },
      "&:hover .MuiOutlinedInput-notchedOutline": {
        border: `1px solid #d32f2f `,
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        border: `1px solid #d32f2f `,
      },
    },
  },
};

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(2),
  },
  "& .MuiPaper-root": {
    width: "500px",
    [theme.breakpoints.down("sm")]: {
      margin: 12,
    },
  },
}));

let paramsData = {};

const AddEditCompanyUserDialog = forwardRef(({}, ref) => {
  const [open, setOpen] = useState(false);
  const [allRoles, setAllRoles] = useState([]);
  const [inputVal, setInputVal] = useState({
    profileImg: null,
    userName: "",
    email: "",
    contact: "",
    userRole: "",
    password: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const userData = useSelector((state) => state.userData.user);

  useImperativeHandle(ref, () => ({
    openDialog(params) {
      setOpen(true);
      paramsData = params;
      fetchAllCompanyRole();
      if (params.type === "edit") {
        setInputVal({
          ...inputVal,
          profileImg: params.data.row.profileImg,
          userName: params.data.row.name,
          email: params.data.row.email,
          contact: params.data.row.contact,
          userRole: params.data.row.role._id,
        });
      }
    },
  }));

  const fetchAllCompanyRole = async () => {
    try {
      setIsLoading(true);
      const response = await getAllCompanyRole(userData?.adminId);
      if (response.data.status === "success") {
        const data = response.data.data;
        setAllRoles(data);
      } else {
        handleOpenSnackBar("error", response.data.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setInputVal({
      profileImg: null,
      userName: "",
      email: "",
      contact: "",
      userRole: "",
      password: "",
    });
  };

  const handleAddEditRole = async () => {
    if (!inputVal.profileImg) {
      return;
    } else if (!inputVal.userName) {
      return;
    } else if (!inputVal.email) {
      return;
    } else if (!inputVal.contact) {
      return;
    } else if (!inputVal.userRole) {
      return;
    } else if (paramsData.type === "add" && !inputVal.password) {
      return;
    }

    let payload = {
      name: inputVal.userName,
      email: inputVal.email,
      contact: inputVal.contact,
      role: inputVal.userRole,
      profileImg: inputVal.profileImg,
      ...(paramsData.type == "add" && {
        password: inputVal.password,
        location: userData.location,
        expertise: userData.expertise,
        services: userData.services,
        companyId: userData?.adminId,
        adminId: userData?.adminId,
        subscriptionId: userData?.subscriptionId,
      }),
    };

    try {
      setIsLoading(true);
      const response =
        paramsData?.type == "add"
          ? await createCompanyUser(payload)
          : await updateCompanyUser(paramsData?.data?.row?._id, payload);
      if (response?.data?.status === "success") {
        handleOpenSnackBar("success", response?.data?.message);
        handleClose();
        paramsData?.getData();
      } else {
        handleOpenSnackBar("error", response?.data?.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", t("somethingWentWrong"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleImageChange = async (event) => {
    try {
      setIsLoading(true);
      const formData = new FormData();
      formData.append("image", event.target.files[0]);
      const response = await axios({
        method: "post",
        url: `${baseUrl}image/upload`,
        data: formData,
        headers: { "Content-Type": "multipart/form-data" },
      });
      setInputVal((prevValues) => ({
        ...prevValues,
        profileImg: response?.data?.url,
      }));
      handleOpenSnackBar("success", t("imageUploadSuccess"));
    } catch (error) {
      handleOpenSnackBar("error", t("imageUploadFailed"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (eee) => {
    setInputVal({
      ...inputVal,
      [eee.target.name]: eee.target.value,
    });
  };

  return (
    <>
      <AppLoader isVisible={isLoading} />
      <BootstrapDialog
        onClose={handleClose}
        aria-labelledby="customized-dialog-title"
        open={open}
      >
        <Stack direction={"row"} justifyContent={"space-between"}>
          <DialogTitle sx={{ m: 0, p: 2 }} id="customized-dialog-title">
            {paramsData.type === "add" ? t("addUser") : t("editUser")}
          </DialogTitle>

          <IconButton
            onClick={handleClose}
            sx={{
              position: "absolute",
              top: 5,
              right: 5,
            }}
          >
            <CloseIcon />
          </IconButton>
        </Stack>
        <DialogContent dividers>
          <Grid container spacing={2} mt={1}>
            <Grid item xs={12} sx={{ textAlign: "center" }}>
              <UploadImageAvatar
                error={!inputVal.profileImg}
                image={inputVal.profileImg}
                handleImageChange={handleImageChange}
              />
            </Grid>
            <Grid item xs={12}>
              <Label required labelText={t("userName")} htmlFor={"user-name"} />
              <TextInput
                required
                inputError={!inputVal.userName}
                helperText={!inputVal.userName && t("userNameRequired")}
                id={"user-name"}
                placeholder={`${t("enter")} ${t("userName")}`}
                value={inputVal.userName}
                name={"userName"}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12}>
              <Label required labelText={t("contact")} htmlFor={"contact"} />
              <TextInput
                required
                inputError={!inputVal.contact}
                helperText={!inputVal.contact && t("userContactRequired")}
                id={"contact"}
                placeholder={`${t("enter")} ${t("contactNumber")}`}
                value={inputVal.contact}
                name={"contact"}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12}>
              <Label required labelText={t("email")} htmlFor={"email"} />
              <TextInput
                required
                inputError={!inputVal.email}
                helperText={!inputVal.email && t("emailRequired")}
                id={"email"}
                placeholder={`${t("enter")} ${t("email")}`}
                value={inputVal.email}
                name={"email"}
                type={"email"}
                onChange={handleInputChange}
              />
            </Grid>

            <Grid item xs={12}>
              <Label labelText="Select Role" htmlFor={"userRole"} required />
              <FormControl fullWidth error={!inputVal.userRole}>
                <Select
                  name={"userRole"}
                  value={inputVal.userRole}
                  onChange={handleInputChange}
                  sx={{
                    ...useStyle.selectStyle,
                  }}
                  displayEmpty
                >
                  <MenuItem value={""} disabled>
                    {t("selectRole")}
                  </MenuItem>
                  {allRoles?.map((val, _i) => (
                    <MenuItem key={_i} value={val._id}>
                      {val.role}
                    </MenuItem>
                  ))}
                </Select>
                {!inputVal.userRole && (
                  <FormHelperText sx={{ ml: 0 }}>
                    {t("roleRequired")}
                  </FormHelperText>
                )}
              </FormControl>
            </Grid>

            {paramsData.type == "add" && (
              <Grid item xs={12}>
                <Label
                  required
                  labelText={t("password")}
                  htmlFor={"password"}
                />
                <TextInput
                  required
                  inputError={!inputVal.password}
                  helperText={!inputVal.password && t("passwordRequired")}
                  id={"password"}
                  placeholder={`${t("enter")} ${t("password")}`}
                  value={inputVal.password}
                  name={"password"}
                  type={"password"}
                  onChange={handleInputChange}
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <GlobalButton
            variant={"outlined"}
            btnText={"cancel"}
            styleProps={{ width: 100, height: 40 }}
            handleClickBtn={handleClose}
          />
          <GlobalButton
            variant={"contained"}
            btnText={paramsData.type == "add" ? t("addUser") : t("editUser")}
            styleProps={{ width: 180, height: 40 }}
            handleClickBtn={handleAddEditRole}
          />
        </DialogActions>
      </BootstrapDialog>
    </>
  );
});

export default AddEditCompanyUserDialog;
